/**
 * Generates array of numbers from the beggining to end
 * e.g range(1, 3) → [ 1, 2, 3 ]
 */
const range = (begin: number, end: number): number[] => {
  begin = Number(begin)
  end   = Number(end)

  if (Number.isNaN(begin) || Number.isNaN(end)) {
    throw new Error('array.range accepts two aguments, both should be numbers')
  }

  const isDownwards = begin > end
  const length = (isDownwards ? begin - end : end - begin) + 1

  return Array.from(Array(length).keys())
    .map((i) => isDownwards ? begin - i : begin + i)
}

/**
 * Groups array of objects by key
 * e.g [ { a: 'b }, { a: 'c }, { a: 'b } ] → { b: [ { a: 'b' }, { a: 'b' } ]: c { a: 'c' }  }
 */
const groupBy = (array: object[], key: string): object =>
  array.reduce((grouped, item) => {
    (grouped[item[key]] = grouped[item[key]] || []).push(item)
    return grouped
  }, {})

/**
 * Moves specified index to specified position
 * e.g [ 0, 1, 2 ] → [ 1, 0, 2 ]
 */
const moveTo = (array: any[], from: number, to: number): any[] => {
  if (Array.isArray(array)) {
    const newArray = [ ...array ]
    newArray.splice(to, 0, newArray.splice(from, 1)[0])
    return newArray
  }

  throw new Error('array.moveTo accepts only arrays in first argument')
}

/**
 * Swap items by index
 * e.g [ 0, 1, 2, 3, 4, 5 ] → [ 5, 1, 2, 3, 4, 0 ]
 */
const swap = (array: any[], from: number, to: number): any[] => {
  if (Array.isArray(array)) {
    const newArray = [ ...array ]
    const item     = newArray[to]
    newArray[to]   = newArray[from]
    newArray[from] = item

    return newArray
  }

  throw new Error('array.swap accepts only arrays in first argument')
}

/**
 * Removes item by index in array
 * e.g [ 0, 1, 2 ], 1 → [ 0, 2 ]
 */
const removeByIndex = (array: any[], index: number): any[] => {
  const newArray = [ ...array ]

  newArray.splice(index, 1)

  return newArray
}

/**
 * Splits array into chunks with specific size
 * e.g [ 'a', 'b', 'c', 'd' ] => [ [ 'a', 'b' ], [ 'c', 'd' ] ] if chunk size is 2
 */
const toChunks = (array: any[], chunkSize: number): any[][] => {
  const chunksCount = Math.ceil(array.length / chunkSize)

  if (chunksCount === 0) {
    return []
  }

  return range(0, chunksCount - 1).map((chunkIndex) => {
    const startIndex  = chunkIndex * chunkSize
    const endIndex    = startIndex + chunkSize > array.length ? array.length : startIndex + chunkSize

    return array.slice(startIndex, endIndex)
  })
}

/**
 * Shuffles the array randomly
 * e.g [ 0, 1, 2, 3 ] → [ 2, 1, 0, 3 ]
 */
// TODO if return value is same as passed then do shuffle again
const shuffle = (array: any[]): any[] => {
  if (Array.isArray(array)) {
    let newArray = [ ...array ]

    // if array is suit for shuffle, it's small enougth, return it right away
    if (newArray.length <= 1) {
      return newArray
    }

    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [ newArray[i], newArray[j] ] = [ newArray[j], newArray[i] ]
    }

    if (newArray.join() === array.join()) {
      newArray.reverse()
    }

    return newArray
  }

  throw new Error('array.shuffle accepts only arrays in first argument')
}

/**
 * Toggles value in array. If value is in the array it will be removed. If value isn't in the array it will be added.
 */
const toggle = (array: any[] = [], value: any): any[] => {
  const result = []
  let found = false

  for (let i = 0, l = array.length; i < l; i++) {
    if (array[i] !== value) {
      result.push(array[i])
    }
    else {
      found = true
    }
  }

  if (!found) {
    result.push(value)
  }

  return result
}

/**
 * Filters repeated values in array
 */
const unique = (array: any[] = []): any[] => (
  array.reduce((filtered, item) => {
    if (!filtered.includes(item)) {
      filtered.push(item)
    }

    return filtered
  }, [])
)

export default {
  range,
  groupBy,
  moveTo,
  removeByIndex,
  toChunks,
  shuffle,
  toggle,
  unique,
  swap,
}
